/**
 * Practice Screen — Web Layout
 * Split view with lesson list on the left and lesson detail on the right.
 * Multi-column grid for lessons when not in split view.
 * WebContainer constrains content width.
 */

import React, { useCallback, useState } from 'react';
import {
  View,
  ScrollView,
} from 'react-native';
import { Box, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { FS_MD } from '../theme';
import { ErrorState, LoadingState, EmptyState } from '../components/ui';
import { SplitView } from '../components/ui/SplitView';
import { LessonDetailScreen } from './LessonDetailScreen';
import { usePracticeData } from './practice/usePracticeData';
import { RecommendedLessonsCard } from '../components/practice/RecommendedLessonsCard';
import {
  LevelSelector,
  ContinueLessonCard,
  CategorySectionHeader,
  LessonRow,
  FooterTip,
  getLessonState,
} from './practice/PracticeComponents';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

export function PracticeScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const handleLessonSelect = useCallback((slug: string) => {
    setSelectedSlug(slug);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedSlug(null);
  }, []);

  const toggleSection = useCallback((category: string) => {
    setExpandedSections((prev) =>
      prev.has(category) ? new Set() : new Set([category]),
    );
  }, []);

  // On web, always intercept lesson taps — show inline instead of stack navigation
  const data = usePracticeData(handleLessonSelect);

  // ─── Lesson List ───

  const lessonList = (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.background }}
      contentContainerStyle={{ paddingVertical: 24 }}
      showsVerticalScrollIndicator
    >
      <Box pb="$3">
        <LevelSelector
          selectedLevel={data.selectedLevel}
          onLevelPress={data.handleLevelPress}
        />
      </Box>

      {data.lessonPath?.continueLesson && (
        <Box px="$5" mb="$3">
          <ContinueLessonCard
            slug={data.lessonPath.continueLesson.slug ?? ''}
            title={data.lessonPath.continueLesson.title ?? ''}
            category={data.lessonPath.continueLesson.category ?? ''}
            onPress={data.handleContinuePress}
          />
        </Box>
      )}

      <RecommendedLessonsCard onLessonPress={data.handleLessonPress} />

      {data.isLoadingPath && !data.lessonPath && (
        <LoadingState
          testID="practice-loading"
          message={t('practice.loadingPath')}
        />
      )}

      {data.pathError && !data.lessonPath && (
        <ErrorState
          testID="practice-path-error"
          message={t('practice.pathError')}
          retryLabel={t('common.retry')}
          onRetry={data.onRetry}
        />
      )}

      {!data.isLoadingPath && !data.pathError && data.lessonPath && data.sections.length === 0 && (
        <EmptyState
          testID="practice-empty"
          message={t('practice.noLessonsForLevel', { level: data.selectedLevel })}
        />
      )}

      {data.sections.map((section) => (
        <View key={section.slug}>
          <CategorySectionHeader
            section={section}
            isExpanded={expandedSections.has(section.category)}
            onToggle={() => toggleSection(section.category)}
          />
          {expandedSections.has(section.category) && (
            <>
              <View style={{ height: 12 }} />
              <View style={{ paddingHorizontal: 20 }}>
                {section.data.map((item, index) => {
                  const lessonState = getLessonState(item, section.data, index);
                  return (
                    <View key={item.id} style={{ marginBottom: 8 }}>
                      <LessonRow
                        lesson={item}
                        index={index}
                        state={lessonState}
                        onPress={data.handleLessonPress}
                      />
                    </View>
                  );
                })}
              </View>
            </>
          )}
        </View>
      ))}

      {data.sections.length > 0 && <FooterTip />}
    </ScrollView>
  );

  // ─── Detail Panel ───

  const detail = selectedSlug ? (
    <LessonDetailScreen
      key={selectedSlug}
      slug={selectedSlug}
      embedded
      onNavigateToLesson={setSelectedSlug}
    />
  ) : null;

  const detailPlaceholder = (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.background }}>
      <Text allowFontScaling={false} style={{ fontSize: 48 }}>📖</Text>
      <Text
        allowFontScaling={false}
        style={{
          fontSize: FS_MD * fontScale,
          color: theme.textSecondary,
          marginTop: 12,
          ...ffs,
        }}
      >
        {t('practice.selectLesson')}
      </Text>
    </View>
  );

  return (
    <View testID="practice-screen" style={{ flex: 1, backgroundColor: theme.background }}>
      <SplitView
        master={lessonList}
        detail={detail}
        detailPlaceholder={detailPlaceholder}
        backLabel={`← ${t('tabs.practice')}`}
        onBack={handleBack}
      />
    </View>
  );
}
